import { Project, Asset, AppNode, Connection } from '../types';
import { BaseService } from './core/BaseService';

class ProjectApiService extends BaseService {
  async listProjects(page: number = 1, pageSize: number = 20): Promise<{ projects: Project[]; total: number; page: number; page_size: number }> {
    const data = await this.get(`/api/projects?page=${page}&page_size=${pageSize}`);
    return {
      projects: (data as any).projects || [],
      total: (data as any).total || 0,
      page: (data as any).page || 1,
      page_size: (data as any).page_size || 20,
    };
  }

  async createProject(name: string): Promise<Project> {
    const data = await this.post(`/api/projects?name=${encodeURIComponent(name)}`);
    return data as Project;
  }

  async getProject(projectId: string): Promise<Project> {
    const data = await this.get(`/api/projects/${projectId}`);
    return data as Project;
  }

  async updateProject(
    projectId: string,
    data: { name?: string; thumbnail?: string; workflow_data?: { nodes: AppNode[]; connections: Connection[]; groups: any[] } }
  ): Promise<Project> {
    const result = await this.put(`/api/projects/${projectId}`, data);
    return result as Project;
  }

  async deleteProject(projectId: string): Promise<{ deleted: boolean }> {
    const data = await this.delete(`/api/projects/${projectId}`);
    return data as { deleted: boolean };
  }

  async listAssets(projectId: string): Promise<Asset[]> {
    const data = await this.get(`/api/projects/${projectId}/assets`);
    return (data as any).assets || [];
  }

  async uploadAsset(
    projectId: string,
    file: File,
    metadata?: Record<string, any>
  ): Promise<Asset> {
    const formData = new FormData();
    formData.append('file', file);
    if (metadata) {
      formData.append('metadata', JSON.stringify(metadata));
    }
    return this.upload<Asset>(`/api/projects/${projectId}/assets`, formData);
  }

  async downloadAndSaveAsset(
    projectId: string,
    url: string,
    filename?: string,
    metadata?: Record<string, any>
  ): Promise<Asset> {
    const params = new URLSearchParams({ url });
    if (filename) params.append('filename', filename);
    if (metadata) params.append('metadata', JSON.stringify(metadata));
    const data = await this.post(`/api/projects/${projectId}/assets/download?${params}`);
    return data as Asset;
  }

  async deleteAsset(projectId: string, assetId: string): Promise<{ deleted: boolean }> {
    const data = await this.delete(`/api/projects/${projectId}/assets/${assetId}`);
    return data as { deleted: boolean };
  }

  async checkAssetExists(projectId: string, assetId: string): Promise<boolean> {
    const data = await this.get(`/api/projects/${projectId}/assets/${assetId}/exists`);
    return (data as any).exists;
  }

  async exportProject(projectId: string): Promise<void> {
    const response = await fetch(`${this.baseUrl}/api/projects/${projectId}/export`);
    if (!response.ok) throw new Error('导出失败');

    const reader = response.body?.getReader();
    const chunks: Uint8Array[] = [];

    while (reader) {
      const { done, value } = await reader.read();
      if (done) break;
      chunks.push(value);
    }

    const blob = new Blob(chunks as BlobPart[]);
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = 'project.zip';
    a.click();
    URL.revokeObjectURL(blobUrl);
  }

  getAssetUrl(_projectId: string, storagePath: string): string {
    return `/assets/${storagePath}`;
  }
}

export const projectApi = new ProjectApiService();
